import { atom, useAtom, useAtomValue } from "jotai";
import localforage from "localforage";
import { useRouter } from "next/router";

import { useSpotlightState } from "@/custom-hooks/jotai/use-spotlight-state";
import { homePath } from "@/global-state/influencer-app-path";
import { authUserAtom } from "@/global-state/jotai-atom";

export type GlobalDialogType =
  | "NONE"
  | "ACCOUNT_INFO_SETTING"
  | "SNS_ACCOUNT_SETTING"
  | "SNS_PRO_RECOMMENDATION"
  | "SPOTLIGHT_POPUP";

const currentDialogTypeAtom = atom<GlobalDialogType>("NONE");

export const useGlobalDialog = () => {
  const router = useRouter();
  const authUser = useAtomValue(authUserAtom);
  const [currentDialogType, setCurrentDialogType] = useAtom(currentDialogTypeAtom);
  const { popupSpotlight } = useSpotlightState();

  const checkAccountInfoSetting = () => {
    return !(authUser && authUser.displayName && authUser.gender && authUser.areas.length && authUser.phoneNumber);
  };

  const checkSnsAccountSetting = () => {
    return (
      authUser?.status === "active" &&
      !authUser.snsAccounts.some((sns) => sns.accountUrl.replace("https://www.instagram.com/", ""))
    );
  };

  const checkSnsProAccountRecommendation = async () => {
    const lastSuggestProAt = (await localforage.getItem("lastSuggestProAt")) || 0;
    const isPassed24hours = Number(lastSuggestProAt) < new Date().getTime() - 24 * 60 * 60 * 1000;

    const instagramAccount = authUser?.snsAccounts.find((sns) => sns.type === "instagram");
    const instagramAccountDate = new Date((instagramAccount?.createdAt ?? 0) * 1000);
    const freshInstagramAccountDate = new Date("2024-04-14T15:00:00.000Z");
    const hasFreshProAccount = !!instagramAccount?.accessToken && instagramAccountDate >= freshInstagramAccountDate;

    return isPassed24hours && !hasFreshProAccount;
  };

  const checkSpotlightPopup = async () => {
    if (!popupSpotlight?.id) {
      return false;
    }

    const popupSpotlightId = (await localforage.getItem("popupSpotlightId")) || "";
    return popupSpotlightId !== popupSpotlight.id;
  };

  const closeDialog = () => setCurrentDialogType("NONE");

  const updateDialogType = async (dialogNumber?: number) => {
    if (router.pathname !== homePath) {
      return setCurrentDialogType("NONE");
    }

    if ((dialogNumber === undefined || dialogNumber < 1) && checkAccountInfoSetting()) {
      return setCurrentDialogType("ACCOUNT_INFO_SETTING");
    }

    if ((dialogNumber === undefined || dialogNumber < 2) && checkSnsAccountSetting()) {
      return setCurrentDialogType("SNS_ACCOUNT_SETTING");
    }

    if ((dialogNumber === undefined || dialogNumber < 3) && (await checkSnsProAccountRecommendation())) {
      return setCurrentDialogType("SNS_PRO_RECOMMENDATION");
    }

    if ((dialogNumber === undefined || dialogNumber < 4) && (await checkSpotlightPopup())) {
      return setCurrentDialogType("SPOTLIGHT_POPUP");
    }

    return setCurrentDialogType("NONE");
  };

  return {
    currentDialogType,
    closeDialog,
    updateDialogType,
  };
};
